import React, { useState, useRef } from 'react';
import { Upload, X, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const FileUpload = ({ files, setFiles, maxFiles = 10, maxSize = 20 * 1024 * 1024 }) => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const validateFile = (file) => {
    const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error(`${file.name} n'est pas un format valide (PDF, PNG, JPEG uniquement)`);
      return false;
    }
    if (file.size > maxSize) {
      toast.error(`${file.name} dépasse la taille maximale de 20 Mo`);
      return false;
    }
    return true;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  };

  const handleChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
  };

  const handleFiles = (newFiles) => {
    const validFiles = newFiles.filter(validateFile);
    
    if (files.length + validFiles.length > maxFiles) {
      toast.error(`Vous ne pouvez télécharger que ${maxFiles} fichiers maximum`);
      return;
    }

    setFiles([...files, ...validFiles]);
    toast.success(`${validFiles.length} fichier(s) ajouté(s)`);
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
    toast.info('Fichier supprimé');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div data-testid="file-upload-container">
      <div
        data-testid="file-upload-zone"
        className={`file-upload-zone ${dragActive ? 'drag-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          data-testid="file-input"
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg"
          onChange={handleChange}
          style={{ display: 'none' }}
        />
        <Upload className="mx-auto mb-4" size={48} color="#1e3a8a" />
        <h3 className="text-xl font-semibold mb-2" style={{ color: '#1e3a8a' }}>
          Glissez-déposez vos documents ici
        </h3>
        <p className="text-gray-600 mb-4">
          ou cliquez pour sélectionner des fichiers
        </p>
        <p className="text-sm text-gray-500">
          Formats acceptés : PDF, PNG, JPEG • Maximum {maxFiles} fichiers • 20 Mo par fichier
        </p>
      </div>

      {files.length > 0 && (
        <div data-testid="file-list" className="mt-6 space-y-3">
          <h4 className="font-semibold text-gray-800">Fichiers sélectionnés ({files.length})</h4>
          {files.map((file, index) => (
            <div
              key={index}
              data-testid={`file-item-${index}`}
              className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg"
            >
              <div className="flex items-center space-x-3">
                <FileText size={24} color="#1e3a8a" />
                <div>
                  <p className="font-medium text-gray-800">{file.name}</p>
                  <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                </div>
              </div>
              <Button
                data-testid={`remove-file-${index}`}
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
              >
                <X size={20} color="#dc2626" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;